/**
 * Which queries a successful mutation invalidates (`App.tsx`'s `MutationCache.onSuccess`).
 *
 * A tRPC v11 query key (`@trpc/tanstack-react-query`) is `[pathArray, { input?, type }]` —
 * `pathArray` is the dotted procedure path split on `.`, e.g. `['users', 'me']`. The four
 * status/me probes (`users.me`, `users.status`, `audit.status`, `assets.status`) are read on
 * effectively every page and never change as a side effect of a write anywhere else, so a
 * write elsewhere invalidating them would refetch four queries for nothing on every mutation.
 * Everything else stays invalidated, because a write can touch counts and rows well outside
 * its own entity type (adopt writes master from an environment's read, a push changes an
 * environment's status, …) — this predicate is deliberately coarse, not per-entity.
 *
 * Fails open: a key shape this cannot recognise (fewer than two path segments, not an array,
 * …) invalidates rather than silently going stale forever.
 */
const NEVER_INVALIDATE = new Set(['users.me', 'users.status', 'audit.status', 'assets.status']);

export const shouldInvalidate = (queryKey: readonly unknown[]): boolean => {
    const path = queryKey[0];
    if (!Array.isArray(path) || path.length < 2) return true;
    if (!path.every((segment) => typeof segment === 'string')) return true;
    return !NEVER_INVALIDATE.has(path.join('.'));
};

/**
 * Which queries a master delete refetches straight away: what any write would, except
 * `assets.list`. The editor that just deleted its game still holds that game's file list while it
 * navigates away, and refetching it can only fail (`assets.*` serve games in master only) — so a
 * delete marks it stale instead, and it is refetched the next time it is shown.
 */
export const refetchAfterDelete = (queryKey: readonly unknown[]): boolean =>
    shouldInvalidate(queryKey) && !isPath(queryKey, 'assets.list');

const isPath = (queryKey: readonly unknown[], path: string): boolean => {
    const segments = queryKey[0];
    return Array.isArray(segments) && segments.join('.') === path;
};
